import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { attendanceAPI, shiftsAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { notify, reportLoadError } from '../../utils/notify';
import { exportToExcel } from '../../utils/excelExport';
import { can } from '../../utils/permissions';
import { tn } from '../../utils/plural';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';
import CopyWeekDialog from './CopyWeekDialog';
import DayPanel from './DayPanel';
import {
  addDays, cellKey, cellView, employeeTotals, fmtHours, isoWeek, isWorked, kindOf, payLabel, totalsNote,
} from './weekModel';
import { planByCell, planFlag, planLine, plannedHours } from './plannedModel';
import styles from './TimeAttendance.module.css';

const fullName = (e) => `${e.first_name || ''} ${e.last_name || ''}`.trim();

/**
 * Time & Attendance > Week: every employee × Monday to Sunday (Suriname's
 * calendar), Sundays and public holidays marked, totals per employee and
 * per day.
 */
const WeekTab = () => {
  const { t, locale } = useLanguage();
  const tRef = useRef(t);
  tRef.current = t;

  const [start, setStart] = useState(undefined); // undefined = this week
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [payType, setPayType] = useState('');

  const { user } = useContext(AuthContext);
  // Filling empty days needs attendance:create; overwriting also :update
  // (as the backend asks).
  const canCreate = can(user, 'attendance', 'create');
  const canUpdate = canCreate && can(user, 'attendance', 'update');
  const canDelete = can(user, 'attendance', 'delete');
  // The planned shifts, read only, for who may see the planner
  const canSeePlan = can(user, 'schedules', 'view');
  const [plan, setPlan] = useState(null); // the week's planned shifts; null = not shown
  const [selected, setSelected] = useState(() => new Set()); // cellKey(employee, date)
  const anchor = useRef(null); // [row, day] of the last plain or Ctrl click
  const [overwrite, setOverwrite] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // a mark waiting for "yes, overwrite"
  const [copyOpen, setCopyOpen] = useState(false);
  const [panel, setPanel] = useState(null); // { employeeId, date } of the day open in the side panel

  const load = useCallback(async (weekStart) => {
    setLoading(true);
    try {
      const res = await attendanceAPI.getWeek(weekStart);
      setWeek(res.data);
    } catch (error) {
      reportLoadError(error, tRef.current('Could not load the week'), { main: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(start); }, [load, start]);

  const weekStart = week?.start;
  const weekEnd = week?.end;
  useEffect(() => {
    if (!canSeePlan || !weekStart) return undefined;
    let cancelled = false;
    setPlan(null);
    shiftsAPI.list({ from: weekStart, to: weekEnd })
      .then((res) => { if (!cancelled) setPlan(res.data.shifts); })
      .catch((error) => reportLoadError(error, tRef.current('Could not load the planned shifts')));
    return () => { cancelled = true; };
  }, [canSeePlan, weekStart, weekEnd]);

  const planCells = useMemo(() => planByCell(plan), [plan]);
  const planOfEmployee = (employeeId) => week.days.flatMap((d) => planCells.get(cellKey(employeeId, d.date)) || []);

  const recordsByKey = useMemo(() => {
    const map = new Map();
    (week?.records || []).forEach((r) => map.set(cellKey(r.employee_id, r.date), r));
    return map;
  }, [week]);

  const departments = useMemo(
    () => [...new Set((week?.employees || []).map((e) => e.department).filter(Boolean))].sort(),
    [week],
  );

  const rows = useMemo(() => {
    if (!week) return [];
    const q = search.trim().toLowerCase();
    return week.employees
      .filter((e) => !department || e.department === department)
      .filter((e) => !payType || e.payment_type === payType)
      .filter((e) => !q || fullName(e).toLowerCase().includes(q) || (e.position || '').toLowerCase().includes(q))
      .map((e) => {
        const records = week.days.map((d) => recordsByKey.get(cellKey(e.id, d.date))).filter(Boolean);
        return { employee: e, totals: employeeTotals(records, week.days) };
      });
  }, [week, recordsByKey, search, department, payType]);

  const dayTotals = useMemo(() => (week ? week.days.map((d) => {
    let worked = 0;
    let withRecord = 0;
    let hours = 0;
    rows.forEach(({ employee }) => {
      const r = recordsByKey.get(cellKey(employee.id, d.date));
      if (!r) return;
      withRecord += 1;
      if (isWorked(kindOf(r))) {
        worked += 1;
        hours += parseFloat(r.hours_worked) || 0;
      }
    });
    return { worked, withRecord, hours };
  }) : []), [week, rows, recordsByKey]);

  const weekHours = rows.reduce((sum, r) => sum + r.totals.worked, 0);

  // ---- selection and "Mark as..."
  // A new week or other filters start a new selection: hidden days must
  // never be changed by a click on what is shown.
  useEffect(() => {
    setSelected(new Set());
    anchor.current = null;
  }, [week, search, department, payType]);
  useEffect(() => {
    if (week && !leaveTypeId && week.leave_types.length > 0) setLeaveTypeId(String(week.leave_types[0].id));
  }, [week, leaveTypeId]);

  const clearSelection = () => {
    setSelected(new Set());
    anchor.current = null;
  };

  const clickCell = (event, rowIndex, dayIndex) => {
    const keyAt = (r, d) => cellKey(rows[r].employee.id, week.days[d].date);
    const key = keyAt(rowIndex, dayIndex);
    const additive = event.ctrlKey || event.metaKey;
    if (event.shiftKey && anchor.current) {
      // The rectangle between the first cell clicked and this one.
      const [r0, d0] = anchor.current;
      const next = new Set(additive ? selected : []);
      for (let r = Math.min(r0, rowIndex); r <= Math.max(r0, rowIndex); r += 1) {
        for (let d = Math.min(d0, dayIndex); d <= Math.max(d0, dayIndex); d += 1) next.add(keyAt(r, d));
      }
      setSelected(next);
      return;
    }
    anchor.current = [rowIndex, dayIndex];
    if (additive) {
      const next = new Set(selected);
      if (next.has(key)) next.delete(key); else next.add(key);
      setSelected(next);
    } else {
      setSelected(new Set([key]));
      // A plain click also opens the day in the side panel.
      setPanel({ employeeId: rows[rowIndex].employee.id, date: week.days[dayIndex].date });
    }
  };

  const selectedWithRecord = [...selected].filter((key) => recordsByKey.has(key)).length;

  const STATUS_LABEL = { present: 'Present', absent: 'Absent', sick: 'Sick' };
  const markAs = (status) => {
    if (!canCreate || selected.size === 0) return;
    const cells = [...selected].map((key) => {
      const [id, date] = key.split('|');
      return { employee_id: Number(id), date };
    });
    if (!overwrite && selectedWithRecord === cells.length) {
      notify(t('All the selected days already have a record. Tick "Overwrite" to change them.'), 'warning');
      return;
    }
    const leaveType = week.leave_types.find((lt) => String(lt.id) === String(leaveTypeId));
    const request = {
      cells,
      status,
      leave_type_id: status === 'leave' ? Number(leaveTypeId) : undefined,
      overwrite,
      changing: overwrite ? selectedWithRecord : 0,
      filling: cells.length - selectedWithRecord,
      label: status === 'leave' ? (leaveType ? leaveType.name : t('Leave')) : t(STATUS_LABEL[status]),
    };
    if (request.changing > 0) {
      setConfirm(request); // our own dialog, with how many days will change
      return;
    }
    sendMark(request);
  };

  const sendMark = async (request) => {
    setBusy(true);
    try {
      const res = await attendanceAPI.markDays({
        cells: request.cells,
        status: request.status,
        leave_type_id: request.leave_type_id,
        overwrite: request.overwrite,
      });
      notify([
        tn(t, res.data.created, '{count} day filled', '{count} days filled'),
        t('{count} changed', { count: res.data.updated }),
        t('{count} skipped (they already had a record)', { count: res.data.skipped }),
      ].join(', '), 'success');
      setConfirm(null);
      clearSelection();
      await load(week.start);
    } catch (error) {
      notify(error.response?.data?.message || error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const dayLabel = (d) => new Date(`${d.date}T00:00:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric' });
  const rangeLabel = week
    ? `${new Date(`${week.start}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${
      new Date(`${week.end}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}`
    : '';

  const exportWeek = () => {
    if (!week || rows.length === 0) {
      notify(t('Nothing to export'), 'warning');
      return;
    }
    const data = rows.map(({ employee, totals }) => {
      const line = {
        [t('Employee')]: fullName(employee),
        [t('Position')]: employee.position || '',
        [t('Pay')]: payLabel(employee, t, locale),
      };
      week.days.forEach((d) => {
        const view = cellView(recordsByKey.get(cellKey(employee.id, d.date)), t);
        line[`${dayLabel(d)}${d.holiday_name ? ` (${d.holiday_name})` : ''}`] = view.kind === 'empty' ? '' : [view.line1, view.line2, ...view.flags].filter(Boolean).join(' · ');
      });
      line[t('Hours worked')] = Number(totals.worked.toFixed(2));
      if (plan) line[t('Planned hours')] = Number(plannedHours(planOfEmployee(employee.id)).toFixed(2));
      line[t('Notes')] = totalsNote(totals, t);
      return line;
    });
    exportToExcel(data, `attendance_week_${week.start}`, t('Week'));
  };

  if (!week) {
    return (
      <div className={styles.empty}>
        {loading ? t('Loading...') : <button type="button" className="btn btn--secondary" onClick={() => load(start)}>{t('Retry')}</button>}
      </div>
    );
  }

  return (
    <div>
      <div className={styles.weekToolbar}>
        <div className={styles.weekNav}>
          <button type="button" className="btn btn--secondary btn--icon" onClick={() => setStart(addDays(week.start, -7))} aria-label={t('Previous week')}><ChevronLeft aria-hidden="true" /></button>
          <div className={styles.weekLabel}>
            <span className={styles.weekRange}>{rangeLabel}</span>
            <span className={styles.muted}>{t('Week {number} · Monday to Sunday · Suriname time', { number: isoWeek(week.start) })}</span>
          </div>
          <button type="button" className="btn btn--secondary btn--icon" onClick={() => setStart(addDays(week.start, 7))} aria-label={t('Next week')}><ChevronRight aria-hidden="true" /></button>
          <button type="button" className="btn btn--secondary" onClick={() => setStart(undefined)} disabled={week.start <= week.today && week.today <= week.end}>
            {t('This week')}
          </button>
        </div>
        <div className={styles.weekFilters}>
          <label className={styles.field}>
            <span>{t('Search')}</span>
            <input type="search" className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Employee or position')} />
          </label>
          <label className={styles.field}>
            <span>{t('Department')}</span>
            <select className="select" value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">{t('All departments')}</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>{t('Pay type')}</span>
            <select className="select" value={payType} onChange={(e) => setPayType(e.target.value)}>
              <option value="">{t('Hourly and monthly')}</option>
              <option value="hourly">{t('Hourly')}</option>
              <option value="monthly">{t('Monthly')}</option>
            </select>
          </label>
          {canCreate && (
            <button type="button" className="btn btn--secondary" onClick={() => setCopyOpen(true)} disabled={rows.length === 0}>
              {t('Copy previous week…')}
            </button>
          )}
          <button type="button" className="btn btn--secondary" onClick={exportWeek}>{t('Export Excel')}</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className={styles.selectionBar} role="region" aria-label={t('Selection')}>
          <span className={styles.selectionCount}>{tn(t, selected.size, '{count} day selected', '{count} days selected')}</span>
          {selectedWithRecord > 0 && (
            <span className={styles.selectionNote}>
              {tn(t, selectedWithRecord, '{count} already has a record', '{count} already have a record')}
            </span>
          )}
          <span className={styles.grow} />
          {canCreate ? (
            <>
              <span className={styles.selectionNote}>{t('Mark as')}</span>
              <button type="button" className={styles.markBtn} disabled={busy} onClick={() => markAs('present')}>{t('Present')}</button>
              <button type="button" className={styles.markBtn} disabled={busy} onClick={() => markAs('absent')}>{t('Absent')}</button>
              <button type="button" className={styles.markBtn} disabled={busy} onClick={() => markAs('sick')}>{t('Sick')}</button>
              <select
                className={styles.markSelect}
                value={leaveTypeId}
                onChange={(e) => setLeaveTypeId(e.target.value)}
                aria-label={t('Leave type')}
              >
                {week.leave_types.map((lt) => (
                  <option key={lt.id} value={lt.id}>{lt.name}{lt.is_paid ? '' : ` (${t('unpaid')})`}</option>
                ))}
              </select>
              <button type="button" className={styles.markBtn} disabled={busy || !leaveTypeId} onClick={() => markAs('leave')}>{t('Leave')}</button>
              {canUpdate && (
                <label className={styles.overwrite}>
                  <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                  <span>{t('Overwrite the days that already have a record')}</span>
                </label>
              )}
            </>
          ) : (
            <span className={styles.selectionNote}>{t('You can view this week but not change it')}</span>
          )}
          <button type="button" className={styles.clearBtn} onClick={clearSelection}>{t('Clear')}</button>
        </div>
      )}

      {panel && (() => {
        const employee = week.employees.find((e) => Number(e.id) === Number(panel.employeeId));
        const day = week.days.find((d) => d.date === panel.date);
        if (!employee || !day) return null;
        return (
          <DayPanel
            open
            employee={employee}
            day={day}
            record={recordsByKey.get(cellKey(employee.id, day.date))}
            leaveTypes={week.leave_types}
            standardHours={week.standard_hours}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onClose={() => setPanel(null)}
            onSaved={() => {
              setPanel(null);
              load(week.start);
            }}
          />
        );
      })()}

      <CopyWeekDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        weekStart={week.start}
        employeeIds={rows.map((r) => Number(r.employee.id))}
        onCopied={() => {
          setCopyOpen(false);
          load(week.start);
        }}
      />

      <Modal
        open={Boolean(confirm)}
        title={t('Overwrite days?')}
        onClose={() => setConfirm(null)}
        busy={busy}
        size="sm"
        footer={(
          <>
            <button type="button" className="btn btn--secondary" onClick={() => setConfirm(null)} disabled={busy}>{t('Cancel')}</button>
            <button type="button" className="btn btn--danger" onClick={() => sendMark(confirm)} disabled={busy}>
              {tn(t, confirm ? confirm.changing : 0, 'Change {count} day', 'Change {count} days')}
            </button>
          </>
        )}
      >
        {confirm && (
          <p>
            {tn(t, confirm.changing,
              '{count} day that already has a record will be changed to "{status}". Its times stay; status and hours change.',
              '{count} days that already have a record will be changed to "{status}". Their times stay; status and hours change.',
              { status: confirm.label })}
            {confirm.filling > 0 && ` ${tn(t, confirm.filling, '{count} empty day will be filled.', '{count} empty days will be filled.')}`}
          </p>
        )}
      </Modal>

      <div className={styles.tableWrap} aria-busy={loading} onKeyDown={(e) => { if (e.key === 'Escape') clearSelection(); }}>
        <div className={styles.weekGrid} role="table" aria-label={t('Attendance of the week {range}', { range: rangeLabel })}>
          <div role="row" className={styles.weekRow}>
            <div role="columnheader" className={styles.weekHead}>{t('Employee')}</div>
            {week.days.map((d) => (
              <div
                role="columnheader"
                key={d.date}
                className={`${styles.weekHead} ${d.is_holiday ? styles.colHoliday : d.is_sunday ? styles.colSunday : ''}`}
              >
                <div>{dayLabel(d)}{d.date === week.today ? ` · ${t('today')}` : ''}</div>
                <div className={styles.weekHeadNote}>{d.holiday_name || (d.is_sunday ? t('Sunday') : '')}</div>
              </div>
            ))}
            <div role="columnheader" className={`${styles.weekHead} ${styles.colTotal}`}>{t('Week total')}</div>
          </div>

          {rows.map(({ employee, totals }, rowIndex) => (
            <div role="row" key={employee.id} className={styles.weekRow} data-employee={employee.id}>
              <div role="rowheader" className={styles.weekEmployee}>
                <Link className={styles.employeeLink} to={`?tab=employee&employee=${employee.id}&month=${week.start.slice(0, 7)}`}>
                  {fullName(employee)}
                </Link>
                <span className={styles.payLine}>
                  <StatusBadge variant="neutral">
                    {employee.payment_type === 'hourly' ? t('Hourly') : t('Monthly')}
                  </StatusBadge>
                  <span className={styles.muted}>{payLabel(employee, t, locale)}</span>
                </span>
              </div>
              {week.days.map((d, dayIndex) => {
                const key = cellKey(employee.id, d.date);
                const view = cellView(recordsByKey.get(key), t);
                const planned = planCells.get(key) || [];
                const flag = planFlag(planned, view.kind, d.date, week.today, t);
                const planText = planned.length ? `; ${t('planned {shifts}', { shifts: planLine(planned) })}` : '';
                const isSelected = selected.has(key);
                return (
                  <div
                    role="cell"
                    key={d.date}
                    className={`${styles.weekCell} ${d.is_holiday ? styles.colHoliday : d.is_sunday ? styles.colSunday : ''}`}
                    data-kind={view.kind}
                  >
                    <button
                      type="button"
                      className={`${styles.chip} ${styles[`chip_${view.kind}`]} ${isSelected ? styles.chipSelected : ''}`}
                      aria-pressed={isSelected}
                      aria-label={`${fullName(employee)}, ${dayLabel(d)}: ${view.kind === 'empty' ? t('No record') : [view.line1, view.line2, ...view.flags].join(', ')}${planText}${flag ? `; ${flag}` : ''}`}
                      onClick={(e) => clickCell(e, rowIndex, dayIndex)}
                    >
                      {view.kind === 'empty' ? <span aria-hidden="true">—</span> : (
                        <>
                          <span className={styles.chipLine1}>{view.line1}</span>
                          <span className={styles.chipLine2}>{view.line2}</span>
                          {view.flags.map((f) => <span key={f} className={styles.chipFlag}>{f}</span>)}
                        </>
                      )}
                      {planned.length > 0 && <span className={styles.chipPlan} title={planLine(planned)}>{planLine(planned)}</span>}
                      {flag && <span className={styles.chipFlag}>{flag}</span>}
                    </button>
                  </div>
                );
              })}
              <div role="cell" className={`${styles.weekCell} ${styles.colTotal} ${styles.weekTotal}`}>
                <span className={styles.weekTotalHours}>{fmtHours(totals.worked)}</span>
                <span className={styles.muted}>{totalsNote(totals, t)}</span>
                {plan && <span className={styles.muted}>{t('planned {hours}', { hours: fmtHours(plannedHours(planOfEmployee(employee.id))) })}</span>}
              </div>
            </div>
          ))}

          {rows.length === 0 && (
            <div role="row" className={styles.weekRowFull}><div role="cell" className={styles.empty}>{t('No employees match')}</div></div>
          )}

          <div role="row" className={styles.weekRow}>
            <div role="rowheader" className={`${styles.weekHead} ${styles.weekFoot}`}>{t('Worked / with a record')}</div>
            {week.days.map((d, i) => (
              <div role="cell" key={d.date} className={`${styles.weekHead} ${styles.weekFoot} ${d.is_holiday ? styles.colHoliday : d.is_sunday ? styles.colSunday : ''}`}>
                <div>{dayTotals[i].worked} / {dayTotals[i].withRecord}</div>
                <div className={styles.muted}>{fmtHours(dayTotals[i].hours)}</div>
              </div>
            ))}
            <div role="cell" className={`${styles.weekHead} ${styles.weekFoot} ${styles.colTotal}`}>{fmtHours(weekHours)}</div>
          </div>
        </div>
      </div>

      <div className={styles.legend}>
        {[['present', 'Present'], ['night', 'Night shift (ends next day)'], ['absent', 'Absent'], ['sick', 'Sick'], ['leave', 'Paid leave'], ['unpaid', 'Unpaid leave']].map(([kind, label]) => (
          <span key={kind} className={styles.legendItem}><span className={`${styles.legendSwatch} ${styles[`chip_${kind}`]}`} />{t(label)}</span>
        ))}
      </div>
    </div>
  );
};

export default WeekTab;
